import { toast } from "react-toastify";
import { mask, unMask } from "remask";
import { ApiAdimplencia } from "../../../../services/api.js";
import GeraXLSX from "../../../../utils/GeraXLSX.js";

const LIMITE_POR_PAGINA = 100;
const NOME_ARQUIVO_PADRAO = 'Clientes';

/**
 * Formata os registros de contratantes para o dataset da planilha.
 *
 * @param {Array<object>} array - Lista de contratantes retornados pela API.
 * @returns {Array<object>} Registros formatados para exportação.
 */
function formataDataset(array) {
  return array.map((item) => ({
    nome: item.usuario_nome,
    telefone: item.usuario_telefone,
    email: item.usuario_email,
    documento: mask(unMask(item.usuario_documento), ['999.999.999-99', '99.999.999/9999-99']),
    beneficiario: item.beneficiario,
    grupo: item.grupo_descricao || '',
    rua: item.endereco?.endereco_rua || '',
    numero: item.endereco?.endereco_numero || '',
    complemento: item.endereco?.endereco_complemento || '',
    bairro: item.endereco?.endereco_bairro || '',
    cidade: item.endereco?.endereco_cidade || '',
    estado: item.endereco?.endereco_estado || '',
    cep: item.endereco?.endereco_cep || '',
  }));
}

/**
 * Busca todos os contratantes que atendem aos filtros, paginando a API
 * até obter o conjunto completo (ignorando a paginação da tela).
 *
 * @param {Record<string, string|number|boolean>} filtros - Filtros atuais (grupo, tipo de busca, status, etc.).
 * @returns {Promise<Array<object>>} Lista completa de contratantes filtrados.
 */
async function buscarTodosContratantesFiltrados(filtros) {
  const todos = [];
  let page = 0;
  let temMais = true;

  while (temMais) {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(LIMITE_POR_PAGINA),
      ...Object.fromEntries(
        Object.entries(filtros).filter(([, v]) => v !== undefined && v !== null && v !== '')
      ),
    });

    const { data } = await ApiAdimplencia.get(`/contratante?${params.toString()}`);
    const dados = data?.dados ?? data;
    const results = dados?.results ?? [];

    todos.push(...(Array.isArray(results) ? results : []));

    if (results.length < LIMITE_POR_PAGINA) {
      temMais = false;
    } else {
      page += 1;
    }
  }

  return todos;
}

/**
 * Executa a exportação direta em XLS de todos os contratantes do resultado atual,
 * respeitando os filtros aplicados na tela e ignorando a paginação.
 *
 * @param {Record<string, string|number|boolean>} filtros - Filtros atuais (buildFilters da listagem).
 * @param {{ labelGrupoNoContratante: string }} terminologia - Objeto com rótulos (ex.: labelGrupoNoContratante).
 * @returns {Promise<void>}
 */
export async function executarExportacaoDireta(filtros, terminologia) {
  try {
    const registros = await buscarTodosContratantesFiltrados(filtros);

    if (registros.length === 0) {
      toast.info('Nenhum registro para exportar com os filtros aplicados.');
      return;
    }

    const dadosFormatados = formataDataset(registros);
    const groupColumnHeader = terminologia?.labelGrupoNoContratante ?? 'Grupo';
    const headers = ['Nome', 'Telefone', 'Email', 'Documento', 'Beneficiario', groupColumnHeader, 'Rua', 'Nº', 'Complemento', 'Bairro', 'Cidade'];
    const fields = ['nome', 'telefone', 'email', 'documento', 'beneficiario', 'grupo', 'rua', 'numero', 'complemento', 'bairro', 'cidade'];

    GeraXLSX({
      data: dadosFormatados,
      headers,
      fields,
      titulo: NOME_ARQUIVO_PADRAO,
    });

    toast.success(`Exportação concluída: ${registros.length} registro(s).`);
  } catch (error) {
    toast.error(error?.response?.data?.mensagem || 'Erro ao exportar.');
    if (error?.response) {
      console.error('Exportação contratantes', error.response);
    } else {
      console.error('Exportação contratantes', error);
    }
  }
}
